import { Box, type BoxProps } from '@chakra-ui/react';
import { sanitizeRichText } from '@/lib/rich-text';

/**
 * Renders portal-authored rich text (bold/italic/underline, bullet & numbered
 * lists, alignment, font family/size) on the public site. Sanitizes on every
 * render — content in blob storage was already sanitized at save time, but
 * this is what actually stands between it and dangerouslySetInnerHTML.
 * Drop-in for `<Text>{value}</Text>`: pass the same typography props, plus
 * `html` instead of children.
 */
export function RichText({ html, ...rest }: { html: string } & BoxProps) {
  return (
    <Box
      as="div"
      css={{
        '& p': { margin: 0 },
        '& p + p': { marginTop: '0.85em' },
        // The site-wide preflight reset sets `ol, ul { list-style: none }` —
        // restore real markers so authored lists don't render as indented
        // text only.
        '& ul, & ol': { margin: '0.6em 0', paddingInlineStart: '1.3em' },
        '& ul': { listStyleType: 'disc' },
        '& ol': { listStyleType: 'decimal' },
        '& li': { marginBottom: '0.3em' },
        '& li::marker': { color: 'inherit' },
      }}
      {...rest}
      dangerouslySetInnerHTML={{ __html: sanitizeRichText(html) }}
    />
  );
}
